import {
  type CSSProperties,
  type MouseEvent,
  type MouseEventHandler,
  useMemo,
  useRef,
} from 'react';
import styled from 'styled-components';

import { CenterFlex, Flex, VFlex } from '@/components/base';
import Tooltip from '@/components/Tooltip/TooltipV2';
import { MainText } from '@/components/Typography/NamedTypo';
import Typo from '@/components/Typography/Typo';
import useColor from '@/hooks/domain/useColor';
import { type FeedbackSentiment, sentimentMap } from '@/types/feedbackConst';
import { DATE_FORMAT, getTimezoneDayjs } from '@/utils/time';

const SENTIMENT_CIRCLE_RADIUS = 12;

export type SentimentChartData = {
  alias: string;
  date: string;
  primary?: boolean;
  sentiment: FeedbackSentiment;
  hide?: boolean;
};

type Props = {
  data: SentimentChartData[];
  onCircleClick?: (alias: string) => void;
  chartStyle?: CSSProperties;

  /**
   * The mode of the chart.
   * - 'even': Display data points evenly.
   * - 'uneven': Display data points unevenly based on the time.
   */
  mode: 'even' | 'uneven';
  labels?: (string | undefined)[];
};

export const SentimentTrendChart = (props: Props) => {
  const color = useColor();

  const refsArray = useRef<HTMLDivElement[]>([]);

  const setRef = (element: HTMLDivElement | null) => {
    if (!element) return;
    const index = Number(element.dataset['index']);
    refsArray.current[index] = element;
    return element;
  };

  const totalDuration = useMemo(() => {
    if (props.data.length === 0) {
      return 0;
    }

    return getTimezoneDayjs(props.data[props.data.length - 1].date).diff(
      getTimezoneDayjs(props.data[0].date),
      'second',
    );
  }, [props.data]);

  const onMouseMove: MouseEventHandler = (e: MouseEvent) => {
    // get the closest sentiment circle to the mouse position
    const x = e.clientX;
    const y = e.clientY;

    let closestIndex = -1;
    let closestDistance = Infinity;

    refsArray.current.forEach((ref, index) => {
      if (!ref) return;

      const rect = ref.getBoundingClientRect();
      const distance = Math.sqrt(
        (x - rect.left - rect.width / 2) ** 2 +
          (y - rect.top - rect.height / 2) ** 2,
      );

      if (distance < closestDistance) {
        closestIndex = index;
        closestDistance = distance;
      }
    });

    if (closestIndex === -1) return;

    // if current closest sentiemnt circle is different from the previous one
    resetClosestSentimentCircle();
    activateClosestSentimentCircle(closestIndex);
  };

  const onMouseLeave = () => {
    // reset the opacity of all sentiment circles
    resetClosestSentimentCircle();
  };

  /**
   * Calculates the position of the sentiment circle based on the index.
   * @param index - The index of the data point.
   * @returns The position of the sentiment circle as a percentage.
   */
  const getSentimentCirclePos = (index: number) => {
    // 데이터가 2개 미만일 때 중앙에 위치
    if (props.data.length <= 1) {
      return 50;
    }

    // 데이터가 2개 일 때 33%와 66%에 위치
    if (props.data.length === 2) {
      return (100 / 3) * (index + 1);
    }

    if (props.mode === 'even') {
      return (index / (props.data.length - 1)) * 100;
    } else if (props.mode === 'uneven') {
      if (totalDuration === 0) {
        return 50;
      }

      const startTime = getTimezoneDayjs(props.data[0].date);
      const targetTime = props.data[index].date;

      const targetDuration = getTimezoneDayjs(targetTime).diff(
        startTime,
        'second',
      );
      return (targetDuration / totalDuration) * 100;
    }
  };

  const activateClosestSentimentCircle = (index: number | null) => {
    if (index === null) return;

    const closestSentimentCircle = refsArray.current[index];
    if (!closestSentimentCircle) return;
    closestSentimentCircle.classList.add('active');
  };

  const resetClosestSentimentCircle = () => {
    for (const circleEl of refsArray.current) {
      circleEl.classList.remove('active');
    }
  };

  return (
    <StyledChart
      style={props.chartStyle}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
    >
      <div className="horizontal-axis">
        <Flex className="sentiments-container" a="center">
          {props.data.map((dataItem, index, arr) => {
            const sentiment = dataItem.sentiment || 2;
            return (
              !dataItem.hide && (
                <Flex
                  key={dataItem.alias}
                  style={{
                    position: 'absolute',
                    left: `calc(${getSentimentCirclePos(index)}% - ${
                      SENTIMENT_CIRCLE_RADIUS / 2
                    }px)`,
                  }}
                >
                  <Tooltip
                    style={{
                      backgroundColor: color.gray100,
                      boxShadow: 'none',
                      border: `1px solid ${color.gray300}`,
                      borderRadius: 8,
                    }}
                    tooltipNode={
                      <VFlex g={8}>
                        <MainText dim>
                          {getTimezoneDayjs(dataItem.date).format(
                            DATE_FORMAT.readable,
                          )}
                        </MainText>
                        <Flex a="center" g={8}>
                          {sentimentMap[sentiment].icon}
                          <MainText>{sentimentMap[sentiment].value}</MainText>
                        </Flex>
                      </VFlex>
                    }
                  >
                    <StyledSentimentCircle
                      className="sentiment"
                      ref={setRef}
                      onClick={() => props.onCircleClick?.(dataItem.alias)}
                      style={{
                        backgroundColor: color.sentiment[sentiment],
                      }}
                    />
                  </Tooltip>
                </Flex>
              )
            );
          })}

          <StyledLabelWrapper>
            {props.labels?.map((label, index) => {
              return (
                label && (
                  <Typo
                    key={index}
                    type="label3"
                    className="label"
                    style={{
                      left: `calc(${getSentimentCirclePos(index)}% - ${
                        SENTIMENT_CIRCLE_RADIUS / 2
                      }px)`,
                    }}
                  >
                    {label}
                  </Typo>
                )
              );
            })}
          </StyledLabelWrapper>
        </Flex>
      </div>
    </StyledChart>
  );
};

const StyledChart = styled(CenterFlex)`
  border-radius: 8px;
  height: 38px;
  width: 100%;

  .horizontal-axis {
    height: 1px;
    background-color: ${(props) => props.theme.colors.gray400};
    position: relative;
    padding: 0 16px;
    width: 100%;
  }

  .sentiments-container {
    position: relative;
  }
`;

const StyledSentimentCircle = styled(CenterFlex)`
  aspect-ratio: 1;
  width: ${SENTIMENT_CIRCLE_RADIUS}px;
  border-radius: 50%;
  cursor: pointer;
  opacity: 0.6;
  z-index: 0;
  outline: none;

  .main-sentiment {
    position: absolute;
    top: 50%;
    left: -100%;
    transform: translate(-50%, -50%);
  }

  &.active {
    opacity: 1;
    z-index: 1;
    outline: 1px solid ${(props) => props.theme.colors.gray600};
  }
`;

const StyledLabelWrapper = styled(Flex)`
  width: 100%;
  .label {
    top: 12px;
    position: absolute;
    white-space: nowrap;
    transform: translateX(-50%);
  }
`;
